import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { AccessibilityRole, LayoutChangeEvent, View } from 'react-native'

import { css } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { BenefitGroup } from '@lyrahealth-inc/shared-app-logic/src/models/benefit/Benefit'
import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { CopyToClipboard } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/copyToClipboard/CopyToClipboard'
import { Divider } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/divider/Divider'
import { XIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/XIcon'
import { Image } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/image/Image'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { Title, Size as TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { AccessibilityRolesNative } from '@lyrahealth-inc/ui-core-crossplatform/src/constants'
import { DataTablePagination } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/dataTable/DataTablePagination'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

export type LyraCareService = {
  id: string
  label: string
  referralLink?: string
  description?: string
  coverageDescription?: string
}

type SponsoredBenefitsModalProps = {
  visible: boolean
  clientName: string
  sponsorName?: string
  lyraCareServices: LyraCareService[]
  otherBenefitGroups?: BenefitGroup[]
  onRequestClose: () => void
}

type SponsoredBenefitsTab = 'lyraCareServices' | 'otherBenefits'
type SponsoredBenefit = LyraCareService & { iconUri?: string }

const BENEFITS_PER_PAGE = 5
const MODAL_HEIGHT = 800
const MODAL_PADDING = 32

const ModalContents = styled.View<{ headerHeight: number }>(({ headerHeight }) => ({
  minHeight: `${MODAL_HEIGHT - MODAL_PADDING}px`,
  paddingTop: `${headerHeight}px`,
}))

const ModalHeader = styled.View(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.colors.backgroundPrimary,
  flexDirection: 'row',
  gap: theme.spacing['16px'],
  justifyContent: 'space-between',
  left: `${MODAL_PADDING}px`,
  paddingBottom: theme.spacing['24px'],
  paddingTop: `${MODAL_PADDING}px`,
  position: 'absolute',
  right: `${MODAL_PADDING}px`,
  top: 0,
  zIndex: 1,
}))

const Tabs = styled.View(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing['8px'],
  marginBottom: theme.spacing['24px'],
}))

const Tab = styled(PressableOpacity)<{ selected: boolean }>(({ theme, selected }) => ({
  borderColor: selected ? theme.colors.borderActive : theme.colors.borderDefault,
  borderRadius: '20px',
  borderStyle: 'solid',
  borderWidth: 1,
  paddingHorizontal: theme.spacing['16px'],
  paddingVertical: theme.spacing['8px'],
  backgroundColor: selected ? theme.colors.backgroundSecondary : theme.colors.backgroundPrimary,
}))

const BenefitRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['24px'],
  paddingHorizontal: theme.spacing['16px'],
  paddingVertical: theme.spacing['24px'],
}))

const BenefitIdentity = styled.View<{ hasIcon: boolean }>(({ theme, hasIcon }) => ({
  flexBasis: '32%',
  flexShrink: 0,
  ...(hasIcon && {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing['16px'],
  }),
}))

const BenefitDescription = styled.View({
  flex: 1,
  gap: '4px',
})

const CopyLink = styled.View(({ theme }) => ({
  alignItems: 'flex-start',
  marginTop: theme.spacing['8px'],
}))

const BenefitIcon = styled(Image)({
  height: '40px',
  width: '40px',
})

const EmptyState = styled.View(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '240px',
  padding: theme.spacing['24px'],
}))

const PaginationContainer = styled.View(({ theme }) => ({
  marginTop: 'auto',
  paddingTop: theme.spacing['16px'],
}))

export const SponsoredBenefitsModal: React.FC<SponsoredBenefitsModalProps> = ({
  visible,
  clientName,
  sponsorName,
  lyraCareServices,
  otherBenefitGroups = [],
  onRequestClose,
}) => {
  const { colors } = useTheme()
  const { formatMessage } = useIntl()
  const [selectedTab, setSelectedTab] = useState<SponsoredBenefitsTab>('lyraCareServices')
  const [currentPage, setCurrentPage] = useState(1)
  const [headerHeight, setHeaderHeight] = useState(0)

  const otherBenefits: SponsoredBenefit[] = otherBenefitGroups.flatMap((group) =>
    group.benefits.map((benefit) => ({
      id: benefit.name,
      label: benefit.displayName,
      iconUri: benefit.iconUri,
      description: typeof benefit.description === 'string' ? benefit.description : formatMessage(benefit.description),
    })),
  )
  const selectedBenefits: SponsoredBenefit[] = selectedTab === 'lyraCareServices' ? lyraCareServices : otherBenefits
  const totalPageCount = Math.max(1, Math.ceil(selectedBenefits.length / BENEFITS_PER_PAGE))
  const displayedBenefits = selectedBenefits.slice(
    (currentPage - 1) * BENEFITS_PER_PAGE,
    currentPage * BENEFITS_PER_PAGE,
  )

  useEffect(() => {
    if (visible) {
      setSelectedTab('lyraCareServices')
      setCurrentPage(1)
    }
  }, [visible])

  const selectTab = (tab: SponsoredBenefitsTab) => {
    setSelectedTab(tab)
    setCurrentPage(1)
  }

  const otherBenefitsTabLabel = sponsorName
    ? formatMessage(
        {
          defaultMessage: 'Other {sponsorName} benefits · {count}',
          description: 'Tab label for benefits offered by the client sponsor',
        },
        { sponsorName, count: otherBenefits.length },
      )
    : formatMessage(
        {
          defaultMessage: 'Other benefits · {count}',
          description: 'Tab label for other sponsored benefits when the sponsor is unknown',
        },
        { count: otherBenefits.length },
      )

  const modalTitle = formatMessage(
    {
      defaultMessage: 'Sponsored benefits for {clientName}',
      description: 'Title for a modal listing sponsored benefits available to a client',
    },
    { clientName },
  )

  return (
    <Modal
      visible={visible}
      onRequestClose={onRequestClose}
      closeOnScrim
      scrollable
      scrollableModalHeight={`${MODAL_HEIGHT}px`}
      scrollableModalMaxHeight='90vh'
      scrollableModalWidth='880px'
      scrollableStyle={{ padding: `${MODAL_PADDING}px`, paddingTop: 0 }}
      showCloseIcon={false}
      accessibilityLabel={modalTitle}
      customHeader={
        <ModalHeader onLayout={(e: LayoutChangeEvent) => setHeaderHeight(e.nativeEvent.layout.height)}>
          <Title size={TitleSize.LARGE} text={modalTitle} wrap />
          <PressableOpacity
            onPress={onRequestClose}
            accessibilityLabel={formatMessage({
              defaultMessage: 'Close',
              description: 'Accessibility label for close button',
            })}
            testID={tID('SponsoredBenefitsModal-close')}
          >
            <XIcon size={24} fillColor={colors.iconDefault} />
          </PressableOpacity>
        </ModalHeader>
      }
      modalContents={
        <ModalContents headerHeight={headerHeight} testID={tID('SponsoredBenefitsModal')}>
          <Tabs
            accessibilityRole={AccessibilityRolesNative.TAB_LIST}
            accessibilityLabel={formatMessage({
              defaultMessage: 'Sponsored benefits categories',
              description: 'Screen reader label for the sponsored benefits tabs',
            })}
          >
            <Tab
              selected={selectedTab === 'lyraCareServices'}
              accessibilityRole={AccessibilityRolesNative.TAB}
              accessibilityState={{ selected: selectedTab === 'lyraCareServices' }}
              onPress={() => selectTab('lyraCareServices')}
              testID={tID('SponsoredBenefitsModal-tab-lyra-care-services')}
            >
              <BodyText
                color={selectedTab === 'lyraCareServices' ? colors.textActive : colors.textPrimary}
                text={formatMessage(
                  {
                    defaultMessage: 'Lyra Care Services · {count}',
                    description: 'Tab label for Lyra care services',
                  },
                  { count: lyraCareServices.length },
                )}
              />
            </Tab>
            <Tab
              selected={selectedTab === 'otherBenefits'}
              accessibilityRole={AccessibilityRolesNative.TAB}
              accessibilityState={{ selected: selectedTab === 'otherBenefits' }}
              onPress={() => selectTab('otherBenefits')}
              testID={tID('SponsoredBenefitsModal-tab-other-benefits')}
            >
              <BodyText
                color={selectedTab === 'otherBenefits' ? colors.textActive : colors.textPrimary}
                text={otherBenefitsTabLabel}
              />
            </Tab>
          </Tabs>

          <View accessibilityRole={'tabpanel' as AccessibilityRole}>
            {selectedTab === 'otherBenefits' && otherBenefits.length === 0 ? (
              <EmptyState testID={tID('SponsoredBenefitsModal-other-benefits-empty-state')}>
                <BodyText
                  color={colors.textSecondary}
                  text={formatMessage({
                    defaultMessage: 'No other benefits available',
                    description: 'Empty state when a client has no other sponsored benefits',
                  })}
                />
              </EmptyState>
            ) : (
              displayedBenefits.map((benefit, index) => (
                <React.Fragment key={benefit.id}>
                  {index > 0 && <Divider />}
                  <BenefitRow
                    testID={tID(
                      `SponsoredBenefitsModal-${selectedTab === 'lyraCareServices' ? 'service' : 'other-benefit'}-${benefit.id}`,
                    )}
                  >
                    <BenefitIdentity hasIcon={Boolean(benefit.iconUri)}>
                      {benefit.iconUri && <BenefitIcon source={{ uri: benefit.iconUri }} />}
                      <View>
                        <BodyText text={benefit.label} />
                        {benefit.referralLink && (
                          <CopyLink>
                            <CopyToClipboard
                              copyText={benefit.referralLink}
                              displayText={formatMessage({
                                defaultMessage: 'Copy referral link',
                                description: 'Action to copy a care service referral link',
                              })}
                              displayTextSize={BodyTextSize.SMALL}
                              containerCustomStyle={css`
                                flex-direction: row-reverse;
                                align-items: baseline;
                                gap: 8px;
                              `}
                              customTextStyle={{ marginRight: 0, marginTop: 0, textDecorationLine: 'underline' }}
                            />
                          </CopyLink>
                        )}
                      </View>
                    </BenefitIdentity>
                    <BenefitDescription>
                      {benefit.description && <BodyText size={BodyTextSize.SMALL} text={benefit.description} />}
                      {benefit.coverageDescription && (
                        <BodyText size={BodyTextSize.SMALL} text={benefit.coverageDescription} />
                      )}
                    </BenefitDescription>
                  </BenefitRow>
                </React.Fragment>
              ))
            )}
          </View>

          {selectedBenefits.length > BENEFITS_PER_PAGE && (
            <PaginationContainer testID={tID('SponsoredBenefitsModal-pagination')}>
              <DataTablePagination
                currentPage={currentPage}
                totalPageCount={totalPageCount}
                onFirstPage={() => setCurrentPage(1)}
                onPrevPage={() => setCurrentPage((page) => Math.max(1, page - 1))}
                onPageChange={setCurrentPage}
                onNextPage={() => setCurrentPage((page) => Math.min(totalPageCount, page + 1))}
                onLastPage={() => setCurrentPage(totalPageCount)}
              />
            </PaginationContainer>
          )}
        </ModalContents>
      }
    />
  )
}
